import cn from 'classnames';
import styled from 'styled-components';

import { getTagColor } from '@/entities/vfo/helpers';
import { ActivationStatus, Member } from '@/entities/vfo/types';
import { COLORS } from '@/shared/constants/colors';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';

import { StatusCell } from './StatusCell';
import { UnreadCount } from './UnreadCount';

type AvatarProps = {
  size?: 's36' | 's40' | 's44' | 's64' | 's80';
  onClick?: () => void;
  member: Pick<
    Member,
    'activationStatus' | 'firstName' | 'lastName' | 'tag' | 'isFounder' | 'status'
  >;
  showStatus?: boolean;
  unreadCount?: number;
};

export const Avatar: BFC<AvatarProps> = ({
  member,
  showStatus,
  size = 's36',
  unreadCount,
  onClick,
  className,
  style,
  ...props
}) => {
  const initials =
    member.firstName && member.lastName ? `${member.firstName[0]}${member.lastName[0]}` : null;

  const showAvatar =
    member.activationStatus === ActivationStatus.ACTIVE &&
    initials &&
    (member.tag || member.isFounder);

  return (
    <AvatarStyled
      style={{
        backgroundColor: showAvatar ? getTagColor(member.tag!) : COLORS.systemDark100,
        ...style,
      }}
      className={cn({ isFounder: member.isFounder, [size]: size }, className)}
      onClick={onClick}
      {...props}
    >
      {showAvatar ? (
        <Initials>{initials}</Initials>
      ) : (
        <AvatarEmpty>
          <Icon name="user" />
        </AvatarEmpty>
      )}
      {showStatus && member.status && (
        <StatusCellStyled
          status={member.status}
          activationStatus={member.activationStatus}
          showText={false}
        />
      )}

      {Boolean(unreadCount) && <UnreadCount>{unreadCount}</UnreadCount>}
    </AvatarStyled>
  );
};

export const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }
`;

const AvatarStyled = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${COLORS.white};
  position: relative;
  cursor: pointer;

  &.s36 {
    width: 36px;
    height: 36px;
  }

  &.s40 {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  &.s44 {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }

  &.s64 {
    width: 64px;
    height: 64px;
    font-size: 24px;
    border: 2px solid ${COLORS.white};
  }

  &.s80 {
    width: 80px;
    height: 80px;
    font-size: 24px;
    border: 2px solid ${COLORS.white};
  }

  &.isFounder {
    border: 2px solid ${COLORS.systemTeal300};
    background: ${COLORS.systemTeal100}!important;
    color: ${COLORS.systemTeal300}!important;
  }
`;

const Initials = styled.span`
  font-weight: 450;
`;

const AvatarEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusCellStyled = styled(StatusCell)`
  position: absolute;
  top: 0;
  right: 0;
`;
